import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { StructuredSessionId } from '@backrs_ui/grow-ui/src/models/models';
import {
  officeHoursApi,
  type SessionStatus,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.api';
import { logout } from '@backrs_ui/grow-ui/src/redux/session/logoutThunk';
import { RootState } from '@backrs_ui/grow-ui/src/redux/store';

export type SessionStatusEntry = Readonly<{
  status: SessionStatus;
  sessionId: number | null;
}>;

type SessionStatusEntries = Partial<Record<StructuredSessionId, SessionStatusEntry>>;

// 'seeded' means the one-time bootstrap seed has settled, whether or not it returned data; 'idle'
// is the pre-seed state and also the resting state for users who never seed (e.g. guests).
export type SeedStatus = 'idle' | 'seeding' | 'seeded';

type SessionStatusesState = Readonly<{
  entries: SessionStatusEntries;
  seedStatus: SeedStatus;
}>;

const initialState: SessionStatusesState = { entries: {}, seedStatus: 'idle' };

const sessionStatusesSlice = createSlice({
  name: 'sessionStatuses',
  initialState,
  reducers: {
    setAllSessionStatuses: (state, action: PayloadAction<SessionStatusEntries>) => {
      // Updates that arrived during seeding win over the older seed snapshot.
      state.entries = { ...action.payload, ...state.entries };
    },
    setSessionStatus: (
      state,
      action: PayloadAction<{ sessionType: StructuredSessionId; entry: SessionStatusEntry }>,
    ) => {
      state.entries[action.payload.sessionType] = action.payload.entry;
    },
    setSeedStatus: (state, action: PayloadAction<SeedStatus>) => {
      state.seedStatus = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState);
  },
});

export const { setAllSessionStatuses, setSessionStatus, setSeedStatus } =
  sessionStatusesSlice.actions;
export const sessionStatusesReducer = sessionStatusesSlice.reducer;

export const getSessionStatusEntry = (
  state: RootState,
  sessionType: StructuredSessionId,
): SessionStatusEntry | undefined => state.sessionStatuses.entries[sessionType];

export const getSeedStatus = (state: RootState): SeedStatus => state.sessionStatuses.seedStatus;

// Reverse-maps a session id back to its structured type using the seeded statuses. Only the most
// recent session per type is tracked, so this resolves the latest session of a type, not older ones.
export const getStructuredTypeBySessionId = (
  state: RootState,
  sessionId: number | null,
): StructuredSessionId | null => {
  if (sessionId === null) return null;

  const match = Object.entries(state.sessionStatuses.entries).find(
    ([, entry]) => entry?.sessionId === sessionId,
  );

  return (match?.[0] as StructuredSessionId | undefined) ?? null;
};

// Fetch every structured session's status in one call and seed the cached map.
export const loadSessionStatuses = createAsyncThunk<undefined, undefined>(
  'sessionStatuses/loadAll',
  async (_arg, { dispatch }) => {
    dispatch(setSeedStatus('seeding'));

    try {
      const { statuses } = await dispatch(
        officeHoursApi.endpoints.getSessionStatuses.initiate(undefined, { forceRefetch: true }),
      ).unwrap();

      const entries: SessionStatusEntries = {};

      Object.entries(statuses).forEach(([sessionType, value]) => {
        entries[sessionType as StructuredSessionId] = {
          status: value.status,
          sessionId: value.session_id,
        };
      });

      dispatch(setAllSessionStatuses(entries));
    } finally {
      dispatch(setSeedStatus('seeded'));
    }

    return undefined;
  },
);
